import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components/macro";

import ScrollToTop from "../../../components/utils/ScrollToTop";
import VisuallyHidden from "../../../components/VisuallyHidden";
import { useSelectedCase } from "../utils/hooks";
import ButtonLink from "./ButtonLink";
import { addEstateItemPath } from "./estateLinks";
import ProbateLayout from "./ProbateLayout";
import { Tray, TrayActions, TrayHeader } from "../../../probate/components/tray";
import { Badge } from "../../../probate/components/ui/badge";
import { distributableTotal, formatPence, groupLedger } from "../../../shared/probateEstate/ledger";

const SIDES = [
  { direction: "asset", title: "Assets" },
  { direction: "liability", title: "Liabilities" },
];

const Rows = styled.dl`
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--juno-space-4);
  padding: var(--tray-cell-padding-block, var(--juno-space-4)) 0;
  border-bottom: 1px solid var(--border);
  font-size: var(--tray-cell-title-font-size, var(--text-lg));
  line-height: var(--tray-cell-title-line-height, var(--text-lg--line-height));

  &:last-child {
    border-bottom: 0;
  }

  dt,
  dd {
    margin: 0;
  }
`;

const Total = styled.dd`
  display: flex;
  align-items: center;
  gap: var(--juno-space-3);
  color: var(--muted-foreground);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const EstateValue = styled(Row)`
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  dd {
    font-size: var(--text-xl);
    line-height: var(--text-xl--line-height);
    font-variant-numeric: tabular-nums;
  }
`;

// The ledger counts an item only once the staff app sends it back, so this says the one just added
// is on its way
const Added = styled.p`
  margin: 0;
  padding: var(--tray-cell-padding-block, var(--juno-space-4)) 0 0;
`;

// Screen readers announce a change to a status region, not the text it arrives with, so the message
// goes in after the region is on the page. 100ms is the wait react-aria's announcer uses.
const ANNOUNCE_DELAY_MS = 100;

const formatTotal = (pence) => (pence == null ? "Unknown" : formatPence(pence));

const itemsLabel = (count) => (count === 1 ? " item" : " items");

function LedgerRow({ side: { title, items, total } }) {
  return (
    <Row>
      <dt>{title}</dt>
      <Total>
        {formatTotal(total)}
        {items.length > 0 && (
          <>
            {" "}
            <Badge>
              {items.length}
              <VisuallyHidden>{itemsLabel(items.length)}</VisuallyHidden>
            </Badge>
          </>
        )}
      </Total>
    </Row>
  );
}

// Unknown until both sides have a total, because the staff app's net figure counts an item it has
// no figure for as £0
const estateValue = (ledger, sides) => {
  if (sides.some((side) => side.total == null)) {
    return formatTotal(null);
  }
  if (sides.every((side) => side.items.length === 0)) {
    return formatTotal(0);
  }
  return formatTotal(ledger.totals?.current_net_distributable_pence);
};

export default function ProbateEstate() {
  const selectedCase = useSelectedCase({ caseScreen: "estate" });
  const session = useSelector((state) => state.dashboardState.session);
  const location = useLocation();
  const navigate = useNavigate();
  const [addedEstateItem] = useState(location.state?.addedEstateItem);
  const [announced, setAnnounced] = useState(false);

  useEffect(() => {
    if (addedEstateItem == null) return undefined;
    const timer = setTimeout(() => setAnnounced(true), ANNOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [addedEstateItem]);

  // The history entry keeps its state through a reload or a later Back, so this clears it once
  // read, leaving the message to this visit
  useEffect(() => {
    if (location.state?.addedEstateItem != null) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location, navigate]);

  if (!selectedCase) {
    return null;
  }

  // A case the staff app hasn't sent a ledger for yet holds {}, not an empty list of items
  const sent = selectedCase.ledger?.items != null;
  const groups = groupLedger(selectedCase.ledger);
  const sides = SIDES.map((side) => {
    const items = groups[side.direction].flatMap((group) => group.items);
    return { ...side, items, total: sent ? distributableTotal(items) : null };
  });

  return (
    <>
      <ScrollToTop />
      <ProbateLayout caseReference={selectedCase.case_reference}>
        <Tray variant="card">
          <TrayHeader title="The estate" headingAs="h1" />
          <Rows>
            {sides.map((side) => (
              <LedgerRow key={side.direction} side={side} />
            ))}
            <EstateValue>
              <dt>Estate value</dt>
              <dd>{estateValue(selectedCase.ledger, sides)}</dd>
            </EstateValue>
          </Rows>
          {addedEstateItem && (
            <Added role="status">
              {announced && `We’ve got “${addedEstateItem}”. It’ll count in the estate once we’ve added it.`}
            </Added>
          )}
          {session?.session_type === "client" && (
            <TrayActions>
              <ButtonLink
                to={addEstateItemPath(
                  selectedCase.case_reference,
                  [],
                  `/dashboard/${selectedCase.case_reference}/estate`,
                )}
              >
                Add an item
              </ButtonLink>
            </TrayActions>
          )}
        </Tray>
      </ProbateLayout>
    </>
  );
}
